import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import { Pencil, Trash2, Plus, ChevronUp, ChevronDown } from "lucide-react";
import ApplyLeaveModal from "../../modals/Hrms/ApplyLeaveModal";
import AssignLeaveModal from "../../modals/Hrms/AssignLeaveModal";
import {
  fetchLeaves,
  applyLeave,
  updateLeave,
  deleteLeave,
} from "../../redux/applyLeaveSlice";
import {
  fetchAssignedLeaves,
  assignLeave,
  updateAssignedLeave,
  deleteAssignedLeave,
} from "../../redux/assignLeaveSlice";
import { fetchLmsEmployees } from "../../redux/erpSlice"; // Changed import
import { useDispatch, useSelector } from "react-redux";

const LeaveDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const [expandedCourseIds, setExpandedCourseIds] = useState([]);

  const { leaves, status, error } = useSelector((state) => state.leaves);
  const {
    assignedLeaves,
    status: assignStatus,
    error: assignError,
  } = useSelector((state) => state.assignLeave);
  const { lmsEmployees, loading, error: lmsError } = useSelector((state) => state.lmsEmployees); // Changed selector

  const [isApplyLeaveModalOpen, setIsApplyLeaveModalOpen] = useState(false);
  const [isAssignLeaveModalOpen, setIsAssignLeaveModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentLeave, setCurrentLeave] = useState(null);
  const [isMultipleDays, setIsMultipleDays] = useState(false);

  useEffect(() => {
    dispatch(fetchLeaves());
    dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 })); // Changed to fetchLmsEmployees
    dispatch(fetchAssignedLeaves());
  }, [dispatch]);

  const onApplyLeaveSubmit = async (data) => {
    try {
      if (isEdit) {
        await dispatch(updateLeave({ id: currentLeave.id, updatedData: data })).unwrap();
        toast.success("Leave updated successfully!");
      } else {
        await dispatch(applyLeave(data)).unwrap();
        toast.success("Leave applied successfully!");
      }
      setIsApplyLeaveModalOpen(false);
      reset();
    } catch (error) {
      toast.error("Error processing request.");
    }
  };

  const handleEditLeave = (leave) => {
    setIsEdit(true);
    setCurrentLeave(leave);
    setIsApplyLeaveModalOpen(true);
    setValue("teacherEmail", leave.teacherEmail);
    setValue("leaveType", leave.leaveType);
    setValue("dateFrom", leave.dateFrom ? leave.dateFrom.split("T")[0] : "");
    setValue("dateTo", leave.dateTo ? leave.dateTo.split("T")[0] : "");
    setValue("reason", leave.reason);
    setValue("status", leave.status); // Include status when editing
    setIsMultipleDays(!!leave.dateTo && leave.dateFrom !== leave.dateTo);
  };

  const handleDeleteLeave = async (id) => {
    if (window.confirm("Are you sure you want to delete this leave?")) {
      await dispatch(deleteLeave(id)).unwrap();
      toast.success("Leave deleted successfully!");
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await dispatch(updateLeave({ id, updatedData: { status: newStatus } })).unwrap();
      toast.success(`Leave ${newStatus.toLowerCase()} successfully!`);
    } catch (error) {
      toast.error("Failed to update leave status.");
    }
  };

  // Helper function to get emoji based on leave type
  const getLeaveTypeEmoji = (leaveType) => {
    const type = leaveType.toLowerCase();
    if (type.includes('annual') || type.includes('annual leave')) return "🎈";
    if (type.includes('sick') || type.includes('sick leave')) return "🩺";
    if (type.includes('casual') || type.includes('casual leave')) return "🎉";
    if (type.includes('maternity')) return "👶";
    if (type.includes('paternity')) return "👨‍👩‍👧‍👦";
    if (type.includes('hajj') || type.includes('ziarat')) return "🕋";
    return "📅"; // Default emoji
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "text-green-600 bg-green-100";
      case "Rejected":
        return "text-red-600 bg-red-100";
      case "Pending":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const onAssignLeaveSubmit = async (data) => {
    try {
      if (isEdit && currentLeave) {
        await dispatch(
          updateAssignedLeave({ id: currentLeave.id, updatedData: data })
        ).unwrap();
        toast.success("Assigned leave updated successfully!");
      } else {
        await dispatch(assignLeave(data)).unwrap();
        toast.success("Leave assigned successfully!");
      }
      setIsAssignLeaveModalOpen(false);
      reset();
    } catch (error) {
      toast.error(error || "Error processing request.");
    }
  };

  const handleDeleteAssignedLeave = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this assigned leave?")
    ) {
      await dispatch(deleteAssignedLeave(id)).unwrap();
      toast.success("Assigned leave deleted successfully!");
    }
  };

  const handleEditAssignedLeave = (leave) => {
    setIsEdit(true);
    setCurrentLeave(leave);
    setIsAssignLeaveModalOpen(true);
    setValue("teacherEmail", leave.teacherEmail);
    setValue("leaveType", leave.leaveType);
    setValue("numberOfLeave", leave.numberOfLeave);
  };

  const toggleExpand = (id) => {
    setExpandedCourseIds((prevIds) =>
      prevIds.includes(id)
        ? prevIds.filter((courseId) => courseId !== id)
        : [...prevIds, id]
    );
  };

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-start sm:space-x-4 w-full mb-4 space-y-4 sm:space-y-0">
        <button
          className="bg-[#241763] text-white p-2 rounded flex items-center"
          onClick={() => {
            setIsEdit(false);
            setIsApplyLeaveModalOpen(true);
            reset();
          }}
        >
          <Plus className="h-4 w-4" />
          <span className="ml-2 text-sm">Apply Leave</span>
        </button>
        <button
          className="w-full sm:w-auto flex items-center justify-center bg-[#241763] text-white p-2 rounded"
          onClick={() => {
            setIsEdit(false);
            setIsAssignLeaveModalOpen(true);
            reset();
          }}
        >
          <Plus className="h-4 w-4" />
          <span className="ml-2 text-sm">Assign Leave</span>
        </button>
        <button
          className="w-full sm:w-auto flex items-center justify-center bg-[#241763] text-white p-2 rounded"
          onClick={() => setIsModalOpen(true)}
        >
          <span className="ml-2 text-sm">Show Assigned Leaves</span>
        </button>
        <button
          className="w-full sm:w-auto flex items-center justify-center bg-[#241763] text-white p-2 rounded"
          onClick={() => navigate("/employee_all_leaves")}
        >
          <span className="ml-2 text-sm">View Request</span>
        </button>
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-4 items-center w-full">
        <select className="p-2 border border-gray-300 rounded w-full md:w-2/6">
          <option value="">Leave Type</option>
        </select>
        <select className="p-2 border border-gray-300 rounded w-full md:w-2/6">
          <option value="">All Departments</option>
        </select>
        <select className="p-2 border border-gray-300 rounded w-full md:w-2/6">
          <option value="">All Designation</option>
        </select>
        <select className="p-2 border border-gray-300 rounded w-full md:w-2/6">
          <option value="">All Locations</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr>
              <th>Teacher Name</th>
              <th>Leave Type</th>
              <th>Date From</th>
              <th>Date To</th>
              <th>Reason</th>
              <th>Status</th> {/* New Status column */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {leaves.map((leave, index) => {
              const employee = lmsEmployees.find(
                (emp) => emp.email === leave.teacherEmail
              );
              return (
                <React.Fragment key={leave.id}>
                  <tr className="p-2 py-2 hover:bg-gray-200 space-y-2">
                    <td className="py-4">
                      {employee?.firstName} {employee?.lastName}
                    </td>
                    <td className="capitalize">
                      {leave.leaveType.replace(/([A-Z])/g, " $1").trim()}
                    </td>
                    <td>
                      {new Date(leave.dateFrom).toLocaleDateString("en-GB")}
                    </td>
                    <td>
                      {leave.dateTo
                        ? new Date(leave.dateTo).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td>{leave.reason || "N/A"}</td>
                    <td className="capitalize">
                      <span
                        className={`px-2 py-1 rounded ${
                          leave.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : leave.status === "Rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {leave.status}
                      </span>
                    </td>
                    <td className="flex space-x-2">
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
                        onClick={() => handleEditLeave(leave)}
                      >
                        <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                      </button>
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                        onClick={() => handleDeleteLeave(leave.id)}
                      >
                        <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                      </button>
                      {leave.status === "Pending" && (
                        <>
                          <button
                            className="bg-green-500 text-white p-2 rounded hover:bg-green-600"
                            onClick={() => handleStatusUpdate(leave.id, "Approved")}
                          >
                            Approve
                          </button>
                          <button
                            className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                            onClick={() => handleStatusUpdate(leave.id, "Rejected")}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                        onClick={() => toggleExpand(leave.id)}
                      >
                        <ChevronDown
                          className={`w-4 h-4 group-hover:text-white transition-transform ${
                            expandedCourseIds.includes(leave.id)
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                  {expandedCourseIds.includes(leave.id) && (
                    <tr>
                      <td colSpan="7" className="p-4 bg-gray-100">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                          aa
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {leaves.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center p-4 text-gray-500">
                  No leaves applied yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ApplyLeaveModal
        isModalOpen={isApplyLeaveModalOpen}
        isEdit={isEdit}
        onSubmit={onApplyLeaveSubmit}
        handleSubmit={handleSubmit}
        setIsModalOpen={setIsApplyLeaveModalOpen}
        register={register}
        reset={reset}
        employees={lmsEmployees} // Changed from teachers to lmsEmployees
        isMultipleDays={isMultipleDays}
        setIsMultipleDays={setIsMultipleDays}
      />

      <AssignLeaveModal
        isModalOpen={isAssignLeaveModalOpen}
        isEdit={isEdit}
        onSubmit={onAssignLeaveSubmit}
        handleSubmit={handleSubmit}
        setIsModalOpen={setIsAssignLeaveModalOpen}
        register={register}
        reset={reset}
        watch={watch}
      />

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 h-[80vh] max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Assigned Leaves</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                ✖
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border">
                <thead className="bg-gray-200">
                  <tr>
                    <th>Teacher Name</th>
                    <th>Leave Type</th>
                    <th>Total Leaves</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedLeaves.map((assigned) => {
                    const employee = lmsEmployees.find(
                      (emp) => emp.email === assigned.teacherEmail
                    );
                    return (
                      <tr key={assigned.id} className="border">
                        <td className="p-2">
                          {employee?.firstName} {employee?.lastName}
                        </td>
                        <td className="p-2">
                          {assigned.leaveType.replace(/([A-Z])/g, " $1").trim()}
                        </td>
                        <td className="p-2">
                          {(() => {
                            const approvedLeaves = leaves
                              .filter((leave) => leave.teacherEmail === assigned.teacherEmail && 
                                                 leave.leaveType === assigned.leaveType && 
                                                 leave.status === "Approved")
                              .reduce((total, leave) => {
                                const dateFrom = new Date(leave.dateFrom);
                                const dateTo = leave.dateTo ? new Date(leave.dateTo) : dateFrom;
                                const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
                                return total + Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                              }, 0);
                            
                            // Calculate original total by adding back the approved leaves to current balance
                            const originalTotal = assigned.numberOfLeave + approvedLeaves;
                            return originalTotal;
                          })()}
                        </td>
                        <td className="p-2 flex space-x-2">
                          <button
                            className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
                            onClick={() => handleEditAssignedLeave(assigned)}
                          >
                            <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                          </button>
                          <button
                            className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                            onClick={() => handleDeleteAssignedLeave(assigned.id)}
                          >
                            <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default LeaveDetails;